import type {
  DialogActionsProps,
  DialogContentProps,
  DialogProps,
  DialogTitleProps
} from '@material-ui/core';
import { Theme } from '@zinc/ui';
import { bind } from 'bind-decorator';
import type { Lambda } from 'mobx';
import { observe } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { cache } from 'tm3-core/cache';
import type { IAlertDialogProps } from './controls/alert';
import AlertDialog from './controls/alert';
import type { IErrorDialogProps } from './controls/error';
import ErrorDialog from './controls/error';
import type { ISaveCheckDialogProps } from './controls/save-check';
import SaveCheckDialog from './controls/save-check';
import type { IYesNoDialogProps } from './controls/yesno';
import YesNoDialog from './controls/yesno';
import type { IYesNoCancelDialogProps } from './controls/yesnocancel';
import YesNoCancelDialog from './controls/yesnocancel';
import Store, { DialogType } from './stores';

export enum Result {
  yes,
  no,
  cancel
}

export interface IBaseProps {
  className?: string;
  dialogProps?: DialogProps;
  title: React.ReactNode;
  titleProps?: DialogTitleProps;
  contents: React.ReactNode;
  contentProps?: DialogContentProps;
  actionProps?: DialogActionsProps;
  open: boolean;

  errors?: string[];
  errorsTitle?: string;
  errorsOkLabel?: string;
  errorsCancelLabel?: string;

  onErrorsOk?: () => void;
  onErrorsCancel?: () => void;
}

export interface IBaseCall {
  className?: string;
  dialogProps?: DialogProps;
  title: React.ReactNode;
  titleProps?: DialogTitleProps;
  contents: React.ReactNode;
  contentProps?: DialogContentProps;
  actionProps?: DialogActionsProps;

  errors?: string[];
  errorsTitle?: string;
  errorsOkLabel?: string;
  errorsCancelLabel?: string;
}

export interface IProps {
  className?: string;
}

@observer
class ConfirmationDialogs extends React.Component<IProps> {
  private disposer: Lambda;

  constructor(props: IProps) {
    super(props);
    this.disposer = observe(Store, 'show', (change) => {
      if (change.newValue !== change.oldValue && !change.newValue) {
        Store.reset();
      }
    });
  }

  componentWillUnmount() {
    if (Store.show) {
      Store.forceClose();
    }

    if (!!this.disposer) {
      this.disposer();
    }
  }

  render() {
    const { props, show, type } = Store;
    const { className } = this.props;
    const dialogProps = this.getDialogProps();

    let dialog;
    switch (type) {
      case DialogType.alert:
        dialog = (
          <AlertDialog
            {...(props as IAlertDialogProps)}
            className={className}
            dialogProps={dialogProps}
            open={show}
            onErrorsOk={this.completeYes}
            onErrorsCancel={this.completeCancel}
            onOk={this.completeYes}
          />
        );
        break;
      case DialogType.error:
        dialog = (
          <ErrorDialog
            {...(props as IErrorDialogProps)}
            className={className}
            dialogProps={dialogProps}
            open={show}
            onErrorsOk={this.completeYes}
            onErrorsCancel={this.completeCancel}
          />
        );
        break;
      case DialogType.yesno:
        dialog = (
          <YesNoDialog
            {...(props as IYesNoDialogProps)}
            className={className}
            dialogProps={dialogProps}
            open={show}
            onErrorsOk={this.completeYes}
            onErrorsCancel={this.completeCancel}
            onYes={this.completeYes}
            onNo={this.completeNo}
          />
        );
        break;
      case DialogType.yesnocancel:
        dialog = (
          <YesNoCancelDialog
            {...(props as IYesNoCancelDialogProps)}
            className={className}
            dialogProps={dialogProps}
            open={show}
            onErrorsOk={this.completeYes}
            onErrorsCancel={this.completeCancel}
            onYes={this.completeYes}
            onNo={this.completeNo}
            onCancel={this.completeCancel}
          />
        );
        break;
      case DialogType.saveCheck:
        dialog = (
          <SaveCheckDialog
            {...(props as ISaveCheckDialogProps)}
            className={className}
            dialogProps={dialogProps}
            open={show}
            onErrorsOk={this.completeYes}
            onErrorsCancel={this.completeCancel}
            onYes={this.completeYes}
            onNo={this.completeNo}
            onCancel={this.completeCancel}
          />
        );
        break;
      default:
        dialog = null;
        break;
    }

    return <Theme name={cache.productName}>{dialog}</Theme>;
  }

  @bind
  private completeYes(): void {
    Store.complete(Result.yes);
  }

  @bind
  private completeNo(): void {
    Store.complete(Result.no);
  }

  @bind
  private completeCancel(): void {
    Store.complete(Result.cancel);
  }

  private getDialogProps(): DialogProps | undefined {
    if (!Store.props?.dialogProps) {
      return { disablePortal: true };
    }

    return {
      ...Store.props.dialogProps,
      disablePortal: true
    };
  }
}

export default ConfirmationDialogs;
